import Sidebar from "./sidebar";
import { Header } from "../../components/NavBar";
import { MdKeyboardArrowRight } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useContext } from "react";
import {
  NotificationElement,
  NotificationType,
} from "../../components/NotificationComponents";
import { RiArrowDropDownFill } from "react-icons/ri";
import { AccountContext } from "../../contexts/AccountContexts";
import { PaginationComponent } from "../../components/DeveloperComponents";
import { requestNotifications } from "../../requests/notification_requests";
import { ContentContext } from "../../contexts/ContentContext";
import { PageHeader } from "../../components/HeaderComponents";
import { useTranslation } from "react-i18next";
import { NotificationContext } from "../../contexts/NotificationContexts";
import { pageLoadingActive } from "../../components/Background";
import { BreadCrumbsGeneral } from "../../components/NavigationComponents";

export const NotificationsPage = () => {
  const { t } = useTranslation("common");
  const navigator = useNavigate();

  const { user } = useContext(AccountContext);
  const { openErrorModal } = useContext(ContentContext);
  const { socket, isSidebar, setIsSidebar } = useContext(NotificationContext);

  const [numberUnread, setNumberUnread] = useState(0);
  const [numberTotal, setNumberTotal] = useState(0);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [notificationTypesNames, setNotificationTypesNames] = useState([]);
  const [selectedType, setSelectedType] = useState("All");
  const [notifications, setNotifications] = useState([]);
  const [showUnread, setShowUnread] = useState(false);

  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentEntries, setCurrentEntries] = useState([]);
  const [indexOfFirstEntry, setIndexOfFirstEntry] = useState(0);
  const [indexOfLastEntry, setIndexOfLastEntry] = useState(0);

  const [doSearch, setDoSearch] = useState(false);

  useEffect(() => {
    let _currentEntries = notifications
      .filter(
        (notification) =>
          selectedType === "All" || selectedType === notification.type,
      )
      .filter((notification) => (showUnread ? !notification.read : true))
      .slice(indexOfFirstEntry, indexOfLastEntry); // Entries for the current page
    setCurrentEntries(_currentEntries);
  }, [
    notifications,
    indexOfFirstEntry,
    indexOfLastEntry,
    showUnread,
    selectedType,
  ]);

  function getSelectedType(selection) {
    setSelectedType(
      notificationTypesNames[
        selection.props ? selection.props.notificationType : selection
      ],
    );
  }

  useEffect(() => {
    if (doSearch) {
      pageLoadingActive(true);
      // warnings have their own box in the header and are not listed here
      requestNotifications({ nFirst: 500, unread_only: showUnread, kind: "others" })
        .then((data) => {
          setNotifications(data.notifications);
          setNumberTotal(data.notifications.length);
        })
        .catch((error) => {
          console.error(error.status, error.message);
          openErrorModal(error);
        })
        .finally(() => {
          pageLoadingActive(false);
          setDoSearch(false);
        });
    }
  }, [doSearch]);

  useEffect(() => {
    if (!isSidebar) {
      setIsSidebar(true);
    }
    setDoSearch(true);
  }, []);

  useEffect(() => {
    let unreadNotifs = notifications.filter((notif) => !notif.read);
    setNumberUnread(unreadNotifs.length);
  }, [notifications]);

  useEffect(() => {
    let arr = [];
    let arrNames = [];
    arr.push("All");
    arrNames.push("All");
    for (let i = 0; i < notifications.length; i++) {
      let notifType = notifications[i].type;
      if (!arrNames.includes(notifType)) {
        arr.push(<NotificationType notificationType={notifType} />);
        arrNames.push(notifType);
      }
    }
    setNotificationTypes(arr);
    setNotificationTypesNames(arrNames);
  }, [notifications]);

  // Visiting a ticket reads its notifications: once per ticket that has unread ones,
  // and the page shows them read right away instead of after a reload.
  const handleMarkAllRead = () => {
    const isMarked = (item) =>
      !item.read && (selectedType === "All" || item.type === selectedType);
    const ticketIds = new Set(notifications.filter(isMarked).map((item) => item.ticket_id));
    ticketIds.forEach((ticket_id) => socket.emit("user_ticket_visit", { ticket_id }));
    setNotifications((prev) =>
      prev.map((item) => (ticketIds.has(item.ticket_id) ? { ...item, read: true } : item)),
    );
  };

  const handleShowUnread = () => {
    setShowUnread((prev) => !prev);
    setDoSearch(true);
  };

  const breadCrumbs = [
    {
      link: "/admin/dashboard",
      name: t("Home"),
    },
    {
      link: null,
      name: t("Notifications"),
    },
  ];

  return (
    <>
      <main className="flex flex-col justify-start items-center w-full py-2 gap-5 pb-10 px-4 h-full">
        <PageHeader pageTitle={t("Notifications")} />
        <div className=" flex w-full  justify-between lg:grid lg:grid-cols-3">
          <div className="flex flex-col flex-1">
            <h1 className="text-xl font-semibold">{t("Notifications")}</h1>
            <BreadCrumbsGeneral elements={breadCrumbs} />
          </div>
        </div>
        <div className="flex flex-col w-full bg-white dark:bg-gray-700  rounded-xl shadow-lg gap-[1px] py-5 h-full">
          <div className="grid grid-cols-1 sm:grid-cols-2  w-full justify-between px-4 py-3">
            <div className="flex gap-3">
              <div
                className={`relative select-none flex gap-2 items-center p-1 border border-gray-300 dark:border-gray-600 rounded-md px-2 cursor-pointer hover:border-primary dark:hover:border-blue-500  ${showUnread ? "ring-2 ring-primary" : ""}`}
                onClick={handleShowUnread}
              >
                <div
                  className={`absolute ltr:right-0 rtl:left-0 rounded-md inline-flex h-full w-full  ${numberUnread > 0 && !showUnread ? "animate-ping2 border-2 border-sky-400" : ""}`}
                ></div>
                <a>{numberUnread}</a>
                <div className="divider divider-horizontal m-0 before:bg-gray-300 after:bg-gray-300 dark:before:bg-gray-500 dark:after:bg-gray-500 before:w-[1px] after:w-[1px]"></div>
                <span>{t("Unread")}</span>
              </div>
              <div className="flex gap-2 items-center p-1 border border-gray-300 dark:border-gray-600 rounded-md px-2">
                <a>{numberTotal}</a>
                <div className="divider divider-horizontal m-0 before:bg-gray-300 after:bg-gray-300 dark:before:bg-gray-500 dark:after:bg-gray-500 before:w-[1px] after:w-[1px]"></div>
                <span>{t("Total")}</span>
              </div>
            </div>

            <div className="flex gap-2 justify-self-end">
              {notificationTypes.length > 1 && (
                <DropdownWithIcons
                  selectOptions={notificationTypes}
                  getData={getSelectedType}
                />
              )}

              <button
                onClick={handleMarkAllRead}
                className="btn btn-sm"
              >
                {t("Mark All Read")}
              </button>
            </div>
          </div>

          <div className="flex flex-col w-full pb-4">
            {currentEntries.map((notification, index) => (
              <NotificationElement
                key={index}
                notification={notification}
                inPage={true}
              />
            ))}
          </div>
          <PaginationComponent
            data={notifications}
            sortedData={notifications
              .filter(
                (notification) =>
                  selectedType === "All" || selectedType === notification.type,
              )
              .filter((notification) =>
                showUnread ? !notification.read : true,
              )}
            entriesPerPage={entriesPerPage}
            setIndexOfFirstEntry={setIndexOfFirstEntry}
            setIndexOfLastEntry={setIndexOfLastEntry}
          />
        </div>
      </main>
    </>
  );
};

const DropdownWithIcons = ({ selectOptions, getData }) => {
  const { t } = useTranslation("common");
  const dropdownRef = useRef(null);
  const optionsRef = useRef(null);

  const [selectedOption, setSelectedOption] = useState(null);

  const [dropdownOpen, setDropdownOpen] = useState(true);

  useEffect(() => {
    setSelectedOption(selectOptions.length > 0 ? selectOptions[0] : null);
  }, [selectOptions]);

  const [dropdownWidth, setDropdownWidth] = useState(null);
  useEffect(() => {
    if (optionsRef.current && selectOptions.length > 0) {
      setDropdownWidth(optionsRef.current.offsetWidth);
      setTimeout(() => {
        setDropdownOpen(false);
      }, 50);
    }
  }, [selectOptions]);

  useEffect(() => {
    // getData((prev)  => selectOptions.indexOf(selectedOption))
    getData(selectOptions.indexOf(selectedOption));
  }, [selectedOption]);

  const handleClick = () => {
    setDropdownOpen((prev) => !prev);
  };

  const handleOnSelect = (option) => {
    setSelectedOption(option);
    setDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {dropdownWidth && (
        <div
          className={`btn btn-sm bg-inherit border min-w-32 relative text-gray-800 dark:text-gray-300 border-slate-300 dark:border-slate-500 hover:border-gray-400 dark:hover:border-gray-400 hover:bg-inherit flex justify-between items-center font-normal ${dropdownOpen ? "outline outline-2 outline-gray-300 dark:outline-gray-700" : ""}`}
          onClick={handleClick}
          style={{ width: dropdownWidth + 10 }}
        >
          {selectedOption.props ? selectedOption : t(selectedOption)}
          <div className="absolute right-2 text-black dark:text-gray-300">
            <RiArrowDropDownFill size={18} />
          </div>
        </div>
      )}
      {dropdownOpen && (
        <div
          className="absolute top-full text-gray-800 dark:text-gray-300 bg-white dark:bg-gray-700  flex flex-col text-sm rounded-b-md border border-gray-300 dark:border-gray-600"
          ref={optionsRef}
          style={{ minWidth: dropdownWidth + 10 }}
        >
          {selectOptions.map((option, index) => (
            <div
              key={index}
              className={`flex items-start px-2 p-1 cursor-pointer  ${selectedOption === option ? "bg-blue-400 text-white dark:bg-blue-800 dark:text-gray-300" : "hover:bg-blue-600 hover:text-white dark:hover:bg-blue-700 dark:hover:text-gray-200"}`}
              onClick={() => {
                handleOnSelect(option);
              }}
            >
              {option.props ? option : t(option)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
